import { type KeyboardEvent, type PointerEvent, useState } from "react";
import { niceMax, ticks } from "@/charts/scale";
import { moneyShort, moneyTick } from "@/plan-logic/lib/format";
import type { Point } from "@/plan-logic/plan-view";

/**
 * A decision's two lines over the years (v39's `decTraj`, with v40's labels): "Your plan today"
 * against "With this decision". The hover readout sits in a strip above the plot, never over the
 * lines (v39): the year, both values, and the difference; it rests on the last year both plans
 * reach. Over the plot there's only a one-pixel rule and two dots. Every value is a plan-view
 * selection from the two engine plans; nothing is projected here. A plan that runs short stops
 * where the engine stopped it, so its line ends there and the readout shows a dash after it.
 *
 * Insurance (v44) draws the event on both lines: "It happens, on the coverage you hold now" in the
 * first slot and "It happens, with the coverage tested" in the second, both solid; the first line
 * takes `--before`, which the insurance result sets to rose. `single` draws only the first line
 * (the coverage tested is what's held now) and drops the second value and the difference.
 */
export type ChartMarker = { x: number; label: string; tone?: "event" };

export function OutlookChart({
  today,
  decision,
  title,
  labels = ["Your plan today", "With this decision"],
  solid = false,
  single = false,
  markers = [],
  ariaLabel,
}: {
  today: Point[];
  decision: Point[];
  /** The strip's heading ("Where it leaves your net worth"). */
  title: string;
  labels?: [string, string];
  /** Both lines solid (insurance, v44); otherwise today's plan is dashed. */
  solid?: boolean;
  /** One line only (`today`'s slot); `decision` is ignored. */
  single?: boolean;
  /** Dotted rules with a label above the plot: retirement, or an insurance event. */
  markers?: ChartMarker[];
  ariaLabel: string;
}) {
  const second = single ? [] : decision;
  const years = [...new Set([...today, ...second].map((p) => p.date.year))].sort((a, b) => a - b);
  const at = (pts: Point[], y: number) => pts.find((p) => p.date.year === y);
  const both = years.filter((y) => at(today, y) && (single || at(second, y)));
  const rest = years.indexOf(both.at(-1) ?? years.at(-1) ?? 0);
  const [hover, setHover] = useState<number | null>(null);
  if (years.length < 2) return <p className="mut">Not enough of the plan to chart yet.</p>;

  const W = 880;
  const H = 278;
  const L = 64;
  const R = 20;
  const T = 42;
  const B = 28;
  const n = years.length - 1;
  const all = [...today, ...second].map((p) => p.value);
  const lo = Math.min(0, ...all);
  const hi = niceMax(Math.max(...all, 1));
  const Xyear = (x: number) => L + ((x - years[0]) / (years[n] - years[0] || 1)) * (W - L - R);
  // Points sit at their year, so a plan with sparse years keeps its time scale.
  const X = (k: number) => Xyear(years[k]);
  const Y = (v: number) => H - B - ((v - lo) / (hi - lo || 1)) * (H - T - B);
  const path = (pts: Point[]) =>
    pts
      .map(
        (p, i) =>
          `${i ? "L" : "M"}${X(years.indexOf(p.date.year)).toFixed(1)} ${Y(p.value).toFixed(1)}`,
      )
      .join(" ");
  const span = years[n] - years[0];
  const xTicks = [...new Set([0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(years[0] + f * span)))];

  const i = hover ?? rest;
  const year = years[i];
  const a = at(today, year)?.value;
  const b = at(second, year)?.value;
  const d = a !== undefined && b !== undefined ? b - a : undefined;

  const move = (ev: PointerEvent<SVGSVGElement>) => {
    const r = ev.currentTarget.getBoundingClientRect();
    if (!r.width) return;
    const vx = ((ev.clientX - r.left) / r.width) * W;
    const yr = years[0] + ((vx - L) / (W - L - R)) * span;
    let best = 0;
    years.forEach((y, k) => {
      if (Math.abs(y - yr) < Math.abs(years[best] - yr)) best = k;
    });
    setHover(best);
  };
  const key = (ev: KeyboardEvent<SVGSVGElement>) => {
    if (ev.key !== "ArrowLeft" && ev.key !== "ArrowRight") return;
    ev.preventDefault();
    setHover(Math.max(0, Math.min(n, i + (ev.key === "ArrowRight" ? 1 : -1))));
  };

  return (
    <div className="dmtj" data-testid="decision-chart">
      <div className="dmtjh">
        <p className="ph">{title}</p>
        <div className="dmtjr" data-testid="decision-readout" aria-live="polite">
          <span className="yr">{year}</span>
          <span className="rv b">
            <i />
            {labels[0]}
            <b>{a === undefined ? "—" : moneyShort(a)}</b>
          </span>
          {!single && (
            <>
              <span className="rv a">
                <i />
                {labels[1]}
                <b>{b === undefined ? "—" : moneyShort(b)}</b>
              </span>
              <span
                className={`gap${d === undefined || Math.abs(d) < 0.5 ? "" : d > 0 ? " up" : " dn"}`}
              >
                {d === undefined
                  ? "Not compared"
                  : Math.abs(d) < 0.5
                    ? "No change"
                    : `${d > 0 ? "+" : "−"}${moneyShort(Math.abs(d))}`}
              </span>
            </>
          )}
        </div>
      </div>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        className="dtjs"
        role="img"
        aria-label={ariaLabel}
        tabIndex={0}
        onPointerMove={move}
        onPointerLeave={() => setHover(null)}
        onBlur={() => setHover(null)}
        onKeyDown={key}
      >
        {ticks(lo, hi, 4).map((t) => (
          <g key={`y${t}`}>
            <line x1={L} x2={W - R} y1={Y(t)} y2={Y(t)} stroke="var(--line2)" />
            <text x={L - 9} y={Y(t) + 4} textAnchor="end" fontSize="11" fill="var(--ink3)">
              {moneyTick(t)}
            </text>
          </g>
        ))}
        {xTicks.map((k) => (
          <text
            key={`x${k}`}
            x={Xyear(k)}
            y={H - 9}
            textAnchor="middle"
            fontSize="11"
            fill="var(--ink3)"
          >
            {k}
          </text>
        ))}
        {markers.map((m) => (
          <g key={m.label} className={m.tone === "event" ? "dz-evt" : "dz-ret"}>
            <line
              x1={Xyear(m.x)}
              x2={Xyear(m.x)}
              y1={T}
              y2={H - B}
              stroke="currentColor"
              strokeWidth="1"
              strokeDasharray={m.tone === "event" ? "3 4" : "2 4"}
            />
            <text
              x={Xyear(m.x)}
              y={T - 11}
              textAnchor="middle"
              fontSize="11.5"
              fontWeight="700"
              fill="currentColor"
            >
              {m.label}
            </text>
          </g>
        ))}
        {today.length > 1 && (
          <path
            d={path(today)}
            fill="none"
            stroke="var(--before)"
            strokeWidth={solid ? 2.5 : 2}
            strokeDasharray={solid ? undefined : "5 5"}
            strokeLinecap="round"
            data-testid="line-today"
          />
        )}
        {second.length > 1 && (
          <path
            d={path(second)}
            fill="none"
            stroke="var(--after)"
            strokeWidth="2.5"
            strokeLinecap="round"
            data-testid="line-decision"
          />
        )}
        {hover !== null && (
          <g pointerEvents="none">
            <line x1={X(i)} x2={X(i)} y1={T} y2={H - B} stroke="var(--ink3)" strokeWidth="1" />
            {a !== undefined && (
              <circle
                cx={X(i)}
                cy={Y(a)}
                r="4.5"
                fill="var(--before)"
                stroke="var(--card)"
                strokeWidth="1.6"
              />
            )}
            {b !== undefined && (
              <circle
                cx={X(i)}
                cy={Y(b)}
                r="4.5"
                fill="var(--after)"
                stroke="var(--card)"
                strokeWidth="1.6"
              />
            )}
          </g>
        )}
        <rect x={L} y={T} width={W - L - R} height={H - T - B} fill="transparent" />
      </svg>
    </div>
  );
}
